import axios from 'axios'
import { getCustomerId, registerAuthAttempt } from 'utils/auth'
import { loadAuthToken } from './auth'
import { JUST_SESSION_EXPIRED_KEY } from 'constants/sessionStorage'
import { AUTH_ERROR_TYPES } from 'constants/errorCodes'
import unleashClient from 'services/unleashClient'
import { getValidOidcUser } from 'auth/factsetOidc'

function redirectToSessionExpired() {
  registerAuthAttempt()
  sessionStorage.setItem(JUST_SESSION_EXPIRED_KEY, 'true')
  window.location.href = window.location.origin
  return new Promise(() => {})
}

/**
 * Handles a rejected API response: redirects to the session-expired interstitial on 401,
 * or refreshes the OIDC token and retries the request on ERR_NETWORK (Kong's CORS response
 * to an expired token).
 * @param {import('axios').AxiosError} error - The error from axios
 * @returns {Promise<never>} Always rejects or suspends — never resolves with a value
 */
async function handleAuthenticationError(error) {
  if (error?.response?.status === 401) {
    const errors = error.response.data?.errors || []
    if (
      errors.some(
        e =>
          e.code === AUTH_ERROR_TYPES.identityLocked || e.code === AUTH_ERROR_TYPES.notConfigured,
      )
    ) {
      throw error
    }

    return redirectToSessionExpired()
  }

  // A network error (no response — Kong returns a CORS error, not a 401) may mean the OIDC
  // token expired. Refresh it and retry, or redirect to session-expired if refresh fails.
  if (!error?.response && error.code === 'ERR_NETWORK') {
    try {
      const user = await getValidOidcUser()
      if (!user) {
        return redirectToSessionExpired()
      }
      const retryConfig = {
        ...error.config,
        headers: { ...error.config.headers, Authorization: `Bearer ${loadAuthToken()}` },
      }
      return axios(retryConfig)
        .then(response => (retryConfig._rawResponse ? response : response.data))
        .catch(retryError => {
          if (retryError?.response?.status === 401) {
            return redirectToSessionExpired()
          }
          throw retryError
        })
    } catch {
      // getValidOidcUser threw — fall through to the plain rethrow below
    }
  }

  throw error
}

/**
 * @param {string} url - Base URL for the API
 * @param {boolean} [rawResponse=false] - Return raw axios response instead of response.data
 * @param {object} [options] - Optional configuration
 * @param {boolean} [options.skipAuthRedirect=false] - When true, a 401 propagates as a plain rejection
 *   instead of triggering the auth error handler (redirect to login) — use for logout where a redirect would block the flow
 * @returns {import('axios').AxiosInstance} Configured axios instance
 */
function httpClient(url, rawResponse = false, { skipAuthRedirect = false } = {}) {
  const token = loadAuthToken()
  const customerId = getCustomerId()
  const customerIdHeader = customerId ? { 'X-Irwin-Customer-Id': customerId } : {}
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {}

  const featureFlags = unleashClient()
    .getAllToggles()
    .filter(toggle => toggle.enabled)
    .map(toggle => toggle.name)
  const featureFlagsHeader = { 'X-Irwin-Feature-Flags': featureFlags.join(',') }

  const API = axios.create({
    baseURL: url,
    responseType: 'json',
    headers: { ...authHeader, ...customerIdHeader, ...featureFlagsHeader },
    _rawResponse: rawResponse,
  })

  API.interceptors.response.use(
    response => (rawResponse ? response : response.data),
    skipAuthRedirect ? undefined : handleAuthenticationError,
  )

  return API
}

export { httpClient }
